import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IButtonStyles, ITheme } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { concatStyleSets, memoizeFunction } from "@fluentui/react";

import { AppToggleButton, getActionButtonStyles } from "@1js/acui-button";
import { isV9ButtonEnabled } from "@1js/acui-experiments";
import { AppThemeConsumer } from "@1js/acui-theme";
import { AppContext } from "@1js/acui-context";

import { animatePropsChanges } from "../../components/PropsAnimator/PropsAnimator";
import { getOnRenderText } from "../onRenderText";
import { getRibbonButtonLabelTransitionStyles } from "../RibbonButton/RibbonButton.styles";
import { getHideLabelAnimationOptions } from "../RibbonButton/RibbonButtonHideLabelAnimation";
import { RibbonToggleButton as RibbonToggleButtonNext } from "./next";
import type { RibbonToggleButtonInternalProps } from "./RibbonToggleButton.props";
import { useV9RibbonControlsEnabled } from "../../../../hooks";

declare let __Delay_FluentV8__: boolean;

const V8RibbonAppToggleButton =
  typeof __Delay_FluentV8__ === "undefined" || !__Delay_FluentV8__
    ? AppToggleButton
    : React.lazy(
        () =>
          import(
            /*webpackChunkName: "commonV8"*/ "@1js/acui-button/lib/AppToggleButton"
          )
      );

const getStyles = memoizeFunction(
  (
    actionButtonStyles?: IButtonStyles,
    transitionStyles?: IButtonStyles,
    customStyles?: IButtonStyles
  ) => {
    return concatStyleSets(actionButtonStyles, transitionStyles, customStyles);
  }
);

class BaseRibbonToggleButton extends React.PureComponent<
  RibbonToggleButtonInternalProps,
  {}
> {
  public render() {
    const transitionStyles = getRibbonButtonLabelTransitionStyles(
      this.props.textContainerInitialWidth,
      this.props.textContainerFinalWidth,
      this.props.hideLabel
    );

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => (
          <React.Suspense fallback={<></>}>
            <V8RibbonAppToggleButton
              {...this.props}
              styles={getStyles(
                getActionButtonStyles(theme),
                transitionStyles,
                this.props.styles
              )}
              onRenderText={
                this.props.onRenderText || getOnRenderText(this.props.size)
              }
              dataAutomationType={
                this.props.dataAutomationType || "RibbonToggleButton"
              }
            />
          </React.Suspense>
        )}
      </AppThemeConsumer>
    );
  }
}

const RibbonToggleButtonCurrent = animatePropsChanges(
  getHideLabelAnimationOptions<RibbonToggleButtonInternalProps>()
)(BaseRibbonToggleButton);

RibbonToggleButtonCurrent.displayName = "RibbonToggleButtonCurrent";

export const RibbonToggleButton = React.forwardRef<
  HTMLButtonElement,
  RibbonToggleButtonInternalProps
>((props, ref) => {
  const appContext = React.useContext(AppContext);
  const isV9RibbonControlsEnabled = useV9RibbonControlsEnabled();
  const isNewButtonEnabled =
    (isV9ButtonEnabled(appContext.isExperimentalFeatureEnabled) ||
      isV9RibbonControlsEnabled) &&
    !props.useLegacyButton;
  return isNewButtonEnabled ? (
    <RibbonToggleButtonNext ref={ref} {...props} />
  ) : (
    <RibbonToggleButtonCurrent ref={ref as any} {...props} />
  );
});

RibbonToggleButton.displayName = "RibbonToggleButton";

export type RibbonToggleButton = typeof RibbonToggleButton.prototype;
